import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import React, { useEffect, useState } from 'react';

const TEAMS_APP_CHANGEBOARDING_POPUPS_MESSAGE_TEMPLATE_ID = 'teams-app-changeboarding-popups';

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export type PlacementRenderProps = {
	dismiss: VoidFunction;
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
} & EligibleMessage;

export type AppProps = {
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
};

type MessageContextWithFeatureId = EligibleMessage & { featureId?: string };

const placementConfig: PlacementBrowserConfig = {
	placementId: 'teams-screen-space-flags',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => (appProps) => {
	const { featureId, children, ...otherAppProps } = appProps;
	const [messages, setMessages] = useState<EligibleMessage<Record<string, never>>[]>([]);

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<Record<string, never>>>;
	}>('/');

	useEffect(() => {
		if (isLoading || error) {
			return;
		}
		setMessages(data.messages);
	}, [data, error, isLoading]);

	if (error || isLoading || messages.length === 0) {
		return null;
	}

	const handleDismiss = (messageInstanceId: string): void => {
		setMessages((prevMessages) => {
			const filteredMessages = prevMessages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);
			return filteredMessages;
		});
	};

	const messageToRender = messages[0];
	if (messageToRender?.messageTemplateId === TEAMS_APP_CHANGEBOARDING_POPUPS_MESSAGE_TEMPLATE_ID) {
		const teamsMessage = messageToRender as MessageContextWithFeatureId;
		if (!featureId || featureId !== teamsMessage.featureId) {
			return null;
		}
	} else if (featureId) {
		// if featureId is provided, but the message is not a teams-app-changeboarding-popups message, return null
		return null;
	}

	const childrenFunction = children || (() => null);

	return (
		<>
			{messageToRender ? (
				<Messages.render
					key={messageToRender.messageInstanceId}
					dismiss={() => handleDismiss(messageToRender.messageInstanceId)}
					{...messageToRender}
					{...otherAppProps}
				>
					{childrenFunction}
				</Messages.render>
			) : null}
		</>
	);
});
